import React, { useState } from 'react';
import { Link } from 'react-scroll';
import { useNavigate } from 'react-router-dom';
import { Menu, X, Phone, Mail, Instagram, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';

const Header = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const changeLanguage = (lng) => {
    const targetPath = lng === 'en' ? '/en' : '/';
    navigate(targetPath);
    // L'effettivo cambio lingua avviene tramite LanguageWrapper in App.js
  };

  const menuItems = [
    { name: t('header.home'), to: 'home' },
    { name: t('header.about'), to: 'about' },
    { name: t('header.method'), to: 'method' },
    { name: t('header.strengths'), to: 'strengths' },
    { name: t('header.team'), to: 'team' },
    { name: t('header.spaces'), to: 'spaces' },
    { name: t('header.schedule'), to: 'schedule' },
    { name: t('header.reviews'), to: 'reviews' },
    { name: t('header.faq'), to: 'faq' },
    { name: t('header.contact'), to: 'contact' }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="home" smooth={true} duration={500} className="cursor-pointer">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/dee4ntwm_inbound608062308470737027.jpg" 
                alt="4 KIDS Logo" 
                className="h-14 w-14 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-[#E91E8C]">4 KIDS</h1>
                <p className="text-xs text-gray-600">Asilo Nido Pesaro</p>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {menuItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                smooth={true}
                duration={500}
                spy={true}
                offset={-80}
                className="text-sm font-medium text-gray-700 hover:text-[#E91E8C] cursor-pointer transition-colors"
                activeClass="text-[#E91E8C]"
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Language Toggle & Contact Icons */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language Switcher */}
            <div className="flex items-center gap-1 mr-2">
              <button
                onClick={() => changeLanguage('it')}
                className={`px-2 py-1 text-sm font-medium rounded transition-colors ${
                  i18n.language === 'it' 
                    ? 'bg-[#E91E8C] text-white' 
                    : 'text-gray-600 hover:bg-pink-50'
                }`}
              >
                IT
              </button>
              <span className="text-gray-400">|</span>
              <button
                onClick={() => changeLanguage('en')}
                className={`px-2 py-1 text-sm font-medium rounded transition-colors ${
                  i18n.language === 'en' 
                    ? 'bg-[#E91E8C] text-white' 
                    : 'text-gray-600 hover:bg-pink-50'
                }`}
              >
                EN
              </button>
            </div>
            
            <a href="tel:3333858164" className="p-2 rounded-full hover:bg-pink-50 transition-colors">
              <Phone className="w-5 h-5 text-[#E91E8C]" />
            </a>
            <a href="mailto:4kidspesaro@gmail.com" className="p-2 rounded-full hover:bg-pink-50 transition-colors">
              <Mail className="w-5 h-5 text-[#E91E8C]" />
            </a>
            <a 
              href="https://instagram.com/4kids.asilonido" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-2 rounded-full hover:bg-pink-50 transition-colors"
            >
              <Instagram className="w-5 h-5 text-[#E91E8C]" />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 text-gray-700" />
            ) : (
              <Menu className="w-6 h-6 text-gray-700" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t">
            <nav className="flex flex-col gap-2">
              {menuItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  smooth={true}
                  duration={500}
                  offset={-80}
                  className="px-4 py-2 text-gray-700 hover:bg-pink-50 hover:text-[#E91E8C] rounded-lg cursor-pointer transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
            
            {/* Mobile Language Switcher */}
            <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t">
              <Globe className="w-4 h-4 text-gray-400" />
              <button
                onClick={() => { changeLanguage('it'); setIsMenuOpen(false); }}
                className={`px-3 py-1 text-sm font-medium rounded ${
                  i18n.language === 'it' 
                    ? 'bg-[#E91E8C] text-white' 
                    : 'text-gray-600 bg-gray-100'
                }`}
              >
                IT
              </button>
              <button
                onClick={() => { changeLanguage('en'); setIsMenuOpen(false); }}
                className={`px-3 py-1 text-sm font-medium rounded ${
                  i18n.language === 'en' 
                    ? 'bg-[#E91E8C] text-white' 
                    : 'text-gray-600 bg-gray-100'
                }`}
              >
                EN
              </button>
            </div>
            
            <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t">
              <a href="tel:3333858164" className="p-2 rounded-full hover:bg-pink-50 transition-colors">
                <Phone className="w-5 h-5 text-[#E91E8C]" />
              </a>
              <a href="mailto:4kidspesaro@gmail.com" className="p-2 rounded-full hover:bg-pink-50 transition-colors">
                <Mail className="w-5 h-5 text-[#E91E8C]" />
              </a>
              <a 
                href="https://instagram.com/4kids.asilonido" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 rounded-full hover:bg-pink-50 transition-colors"
              >
                <Instagram className="w-5 h-5 text-[#E91E8C]" />
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
